import React, { useState, type ReactNode } from "react";
import { Box, Card, Tab, Tabs, Typography } from "@mui/material";
import EventAvailableTwoToneIcon from "@mui/icons-material/EventAvailableTwoTone";
import GroupsTwoToneIcon from "@mui/icons-material/GroupsTwoTone";
import ReceiptTwoToneIcon from "@mui/icons-material/ReceiptTwoTone";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import SuspenseLoader from "src/components/SuspenseLoader";
import B2BInvoiceTable from "../b2binvoice/B2BInvoiceTable";
import type { TB2BData } from "../type/b2bType";
import B2BSingleSessionRequestsTable from "./B2BSingleSessionRequestsTable";
import B2BSingleSessionsTable from "./B2BSingleSessionsTable";

type IProps = {
	b2bDetails: TB2BData;
	loading: boolean;
};

const SingleB2BOtherInformation = ({ b2bDetails, loading }: IProps) => {
	const [query] = useSearchParams();
	const urlValue = query.get("key");
	const [value, setValue] = useState(
		urlValue === "invoice"
			? "invoice"
			: urlValue === "session_requests"
			? "session_requests"
			: "b2b_sessions"
	);
	const navigate = useNavigate();
	const id = useParams();

	const handleTabChange = (_: React.SyntheticEvent, newValue: string) => {
		setValue(newValue);
		const newParams = new URLSearchParams(query);
		newParams.set("key", newValue);
		navigate(`/b2b/list/${id?.singleB2BId}?${newParams.toString()}`);
	};

	let content: ReactNode;
	if (loading) {
		content = <SuspenseLoader />;
	} else if (value === "b2b_sessions") {
		content = (
			<B2BSingleSessionsTable
				sessions={b2bDetails?.b2b_sessions}
			/>
		);
	} else if (value === "session_requests") {
		content = (
			<B2BSingleSessionRequestsTable
				sessionRequests={b2bDetails?.session_requests}
			/>
		);
	} else if (value === "invoice") {
		if (!b2bDetails?.invoices?.length) {
			content = (
				<Card
					elevation={0}
					sx={{
						p: 3,
						my: 1.5,
						textAlign: "center",
						bgcolor: "background.paper",
						border: "1px dashed",
						borderColor: "divider",
						borderRadius: 1.5,
					}}
				>
					<Box
						display="flex"
						flexDirection="column"
						alignItems="center"
						justifyContent="center"
						gap={0.5}
					>
						<ReceiptTwoToneIcon
							sx={{ fontSize: 38, color: "text.secondary", opacity: 0.6 }}
						/>
						<Typography variant="h5" fontWeight={600} color="text.secondary">
							No Invoices Found
						</Typography>
						<Typography variant="caption" color="text.disabled">
							This B2B member does not have any invoices yet.
						</Typography>
					</Box>
				</Card>
			);
		} else {
			content = (
				<B2BInvoiceTable
					loading={loading}
					invoiceList={b2bDetails?.invoices}
					paginationSkip={0}
				/>
			);
		}
	}

	const b2bSessionsCount = b2bDetails?.b2b_sessions?.length ?? 0;
	const sessionCount = b2bDetails?.session_requests?.length ?? 0;
	const invoiceCount = b2bDetails?.invoices?.length ?? 0;

	return (
		<Box sx={{ mt: 2, pb: 1 }}>
			<Box sx={{ mb: 1 }}>
				<Tabs
					value={value}
					onChange={handleTabChange}
					textColor="primary"
					sx={{
						minHeight: "34px",
						"& .MuiTabs-indicator": { display: "none" },
						"& .MuiTabs-flexContainer": { gap: 1 },
						"& .MuiTab-root": {
							color: "#64748B",
							"&:hover": {
								color: "#1E293B",
							},
							"&.Mui-selected": {
								color: "#059669",
								"&:hover": {
									color: "#047857",
								},
							},
						},
					}}
				>
					<Tab
						value="b2b_sessions"
						icon={<EventAvailableTwoToneIcon sx={{ fontSize: "17px !important" }} />}
						iconPosition="start"
						label={`B2B Sessions (${b2bSessionsCount})`}
						sx={{
							minHeight: "34px",
							py: 0.4,
							px: 1.75,
							borderRadius: "6px",
							fontWeight: 600,
							fontSize: "0.8125rem",
							textTransform: "none",
							color: value === "b2b_sessions" ? "#059669" : "#64748B",
							bgcolor:
								value === "b2b_sessions"
									? "rgba(16, 185, 129, 0.12)"
									: "rgba(0, 0, 0, 0.03)",
							"&.Mui-selected": {
								color: "#059669 !important",
								bgcolor: "rgba(16, 185, 129, 0.12)",
							},
							"&:hover": {
								color: (value === "b2b_sessions" ? "#059669" : "#1E293B") + " !important",
								bgcolor:
									value === "b2b_sessions"
										? "rgba(16, 185, 129, 0.16)"
										: "rgba(0, 0, 0, 0.06)",
							},
						}}
					/>
					<Tab
						value="session_requests"
						icon={<GroupsTwoToneIcon sx={{ fontSize: "17px !important" }} />}
						iconPosition="start"
						label={`Session Requests (${sessionCount})`}
						sx={{
							minHeight: "34px",
							py: 0.4,
							px: 1.75,
							borderRadius: "6px",
							fontWeight: 600,
							fontSize: "0.8125rem",
							textTransform: "none",
							color: value === "session_requests" ? "#059669" : "#64748B",
							bgcolor:
								value === "session_requests"
									? "rgba(16, 185, 129, 0.12)"
									: "rgba(0, 0, 0, 0.03)",
							"&.Mui-selected": {
								color: "#059669 !important",
								bgcolor: "rgba(16, 185, 129, 0.12)",
							},
							"&:hover": {
								color: (value === "session_requests" ? "#059669" : "#1E293B") + " !important",
								bgcolor:
									value === "session_requests"
										? "rgba(16, 185, 129, 0.16)"
										: "rgba(0, 0, 0, 0.06)",
							},
						}}
					/>
					<Tab
						value="invoice"
						icon={<ReceiptTwoToneIcon sx={{ fontSize: "17px !important" }} />}
						iconPosition="start"
						label={`Invoices (${invoiceCount})`}
						sx={{
							minHeight: "34px",
							py: 0.4,
							px: 1.75,
							borderRadius: "6px",
							fontWeight: 600,
							fontSize: "0.8125rem",
							textTransform: "none",
							color: value === "invoice" ? "#059669" : "#64748B",
							bgcolor:
								value === "invoice"
									? "rgba(16, 185, 129, 0.12)"
									: "rgba(0, 0, 0, 0.03)",
							"&.Mui-selected": {
								color: "#059669 !important",
								bgcolor: "rgba(16, 185, 129, 0.12)",
							},
							"&:hover": {
								color: (value === "invoice" ? "#059669" : "#1E293B") + " !important",
								bgcolor:
									value === "invoice"
										? "rgba(16, 185, 129, 0.16)"
										: "rgba(0, 0, 0, 0.06)",
							},
						}}
					/>
				</Tabs>
			</Box>
			{content}
		</Box>
	);
};

export default SingleB2BOtherInformation;
